import { useEffect, useId, useRef, useState } from 'react';

export type PaSelectOption = {
  value: string;
  label: string;
};

type PaSelectProps = {
  label: string;
  value: string;
  options: PaSelectOption[];
  placeholder?: string;
  error?: string;
  onChange: (value: string) => void;
};

const PaSelect = ({
  label,
  value,
  options,
  placeholder = '—',
  error,
  onChange,
}: PaSelectProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((o) => o.value === value);
  const display = selected?.label || placeholder;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      className={`public-analyze__field${open ? ' is-open' : ''}${error ? ' is-error' : ''}`}
      ref={rootRef}
    >
      <span className="public-analyze__field-label" id={`${listId}-label`}>
        {label}
      </span>
      <button
        type="button"
        className={`public-analyze__select-trigger${open ? ' is-open' : ''}${
          !selected ? ' is-placeholder' : ''
        }`}
        aria-invalid={error ? true : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${listId}-label`}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="public-analyze__select-value">{display}</span>
        <span className="public-analyze__select-caret" aria-hidden />
      </button>
      {open && (
        <ul
          id={listId}
          className="public-analyze__select-menu"
          role="listbox"
          aria-labelledby={`${listId}-label`}
        >
          {options.map((o) => (
            <li key={o.value || '__empty'} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={`public-analyze__select-option${
                  o.value === value ? ' is-selected' : ''
                }`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PaSelect;
